import React, { Component } from 'react';
import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';

import connector from 'Utils/connector';
import connectAmp, {
  ConnectAmpProps,
} from 'Webapp/shared/app/connectors/connectAmp';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';
import connectAdTargeting, {
  ConnectAdTargetingProps,
} from 'Webapp/shared/app/connectors/connectAdTargeting';

import AdSlot from './ad-slot';
import {
  AD_UNIT_PATHS,
  LEADERBOARD_SIZES,
  LEADERBOARD_SIZE_MAPPING,
  isLeaderboardAdEnabled,
  isBrandSafetyTargetingEnabled,
} from './ads-config';
import { setPageTargeting } from './gpt';
import { brandSafetyTargeting } from './brand-safety';

const StyledLeaderboardWrapper = styled.div({
  padding: `${SPACING.LARGE} ${SPACING.LARGE} 0`,
});

type LeaderboardAdProps = ConnectAmpProps &
  ConnectFeatureFlagsProps &
  ConnectAdTargetingProps;

/**
 * Leaderboard slot rendered directly under the breadcrumb/subnav (see
 * top-navbar.js) on topic pages, magazine/profile section pages, and item
 * (article) pages. Eagerly loaded; 728x90 on desktop/tablet, 320x50 on
 * narrow viewports.
 */
class LeaderboardAd extends Component<LeaderboardAdProps> {
  componentDidMount() {
    this.applyPageTargeting();
  }

  componentDidUpdate(prevProps: LeaderboardAdProps) {
    if (
      prevProps.adPageType !== this.props.adPageType ||
      prevProps.adTopicSlug !== this.props.adTopicSlug
    ) {
      this.applyPageTargeting();
    }
  }

  shouldRender = () => {
    const { isAmp, featureFlags, adPageType } = this.props;
    return !isAmp && !!adPageType && isLeaderboardAdEnabled(featureFlags);
  };

  applyPageTargeting = () => {
    if (!this.shouldRender()) {
      return;
    }
    const { adPageType, adTopicSlug } = this.props;
    setPageTargeting({
      page_type: adPageType,
      topic_slug: adTopicSlug,
    });
  };

  render() {
    if (!this.shouldRender()) {
      return null;
    }
    const { adPageType, adTopicSlug, adBrandSafetyItems, featureFlags } =
      this.props;
    const targeting: Record<string, string | Array<string>> = {};
    if (adPageType) {
      targeting.page_type = adPageType;
    }
    if (adTopicSlug) {
      targeting.topic_slug = adTopicSlug;
    }
    if (isBrandSafetyTargetingEnabled(featureFlags)) {
      Object.assign(targeting, brandSafetyTargeting(adBrandSafetyItems));
    }
    return (
      <StyledLeaderboardWrapper>
        <AdSlot
          name="leaderboard"
          placementFlag="WEB_ADS_LEADERBOARD"
          adUnitPath={AD_UNIT_PATHS.leaderboard}
          sizes={LEADERBOARD_SIZES}
          sizeMapping={LEADERBOARD_SIZE_MAPPING}
          lazy={false}
          targeting={targeting}
        />
      </StyledLeaderboardWrapper>
    );
  }
}

export default connector<LeaderboardAdProps>(
  connectAmp,
  connectFeatureFlags,
  connectAdTargeting,
)(LeaderboardAd);
